/**
 * `@dxf-ai-editor/cad-core` — the shape a heavy `POLYLINE` is actually DRAWN
 * as, and the window / crossing SELECT geometry read off it (@codex PR #785
 * round 8).
 *
 * Split out of `./selectLeaf.ts`'s `"Polyline"` arm: that file dispatches every
 * kind and must stay readable, and this one kind needs three shared truths at
 * once (the drawable topology, the drawn centreline and the drawn band). It is
 * TWO functions because `./bounds.ts` needs the drawn runs too — a
 * `SelectGeometry` carries its points and its box together, so the box has to
 * be a box of the same silhouette or the object disagrees with itself.
 *
 * # The two defects this arm carried, and the ONE sentence that closes both
 *
 * A heavy POLYLINE is not "a list of points a selection rectangle is tested
 * against". Two DXF fields change WHERE it is drawn, and the select geometry
 * honoured neither:
 *
 *   1. **The bulge is authored in the entity's OWN plane.** Group 42 is a
 *      2D-polyline field measured in the plane of the entity's group-210
 *      extrusion, and `heavyPolylineWcsResolver` has already lifted the
 *      vertices OUT of that plane. An OCS lift maps the CHORD correctly but
 *      NOT the bow: for extrusion `(0, 1, 0)` the OCS y axis IS world Z, so
 *      the 5-unit bow of a `(0,0) → (10,0)` bulge-1 semicircle belongs on
 *      world Z — yet running `bulgeArc` on the lifted coordinates (which is
 *      what this arm did) put it on world **Y**, giving select points spanning
 *      `y ∈ [0, +4.924]` for an entity the canvas draws at `y ≡ 0` (the SIGN of
 *      that phantom flipped with the r17 P1 bulge-orientation fix — a positive
 *      group 42 is the CCW sweep — but the phantom itself is unchanged). That is
 *      the identical "fixed the endpoints, not the curve" mistake the RENDER
 *      path made and closed in round 6, and its fix —
 *      {@link polylineCentrelineSamples}: project into the plane, walk the
 *      spans there, lift the finished samples — is reused here verbatim rather
 *      than re-derived.
 *   2. **A width-carrying polyline is drawn as its BAND, not its centreline.**
 *      Groups 40/41 (over the POLYLINE record's own defaults) make each span a
 *      `TRACE`-shaped, mitred, square-capped ribbon centred on the chord
 *      (`batchedLayer.ts::widePolylineLineVertices` — the centreline is not
 *      stroked at all when a band is drawn). A constant-width-4 polyline on
 *      `(0,0) → (10,0)` is drawn across `y ∈ [-2, +2]`; its select geometry
 *      reported `y ∈ [0, 0]`, so a window that plainly contains the visible
 *      ribbon, or a crossing stroke that plainly cuts it, tested a curve
 *      nobody draws. {@link widePolylineBandLoops} is the SAME call the canvas
 *      batcher, the vector plot and `scene3d/raycast/windowSelect.ts` make,
 *      including the frame the offset is built in.
 *
 * Both were latent only because `getSelectGeometry`'s Polyline arm has no live
 * consumer yet — which is a reason to build it on the drawn shape now, not a
 * reason to leave a second answer to "where is this entity?" in the tree.
 *
 * # Why the band REPLACES the centreline rather than joining it
 *
 * Because that is what is on screen: `geometryToLineVertices` tries
 * `widePolylineLineVertices` FIRST and strokes the centreline only when it
 * returns `null` (no width declared, or a band that degenerates to no loops).
 * Emitting both would let a window select an entity by a stroke the drawing
 * does not contain. The same precedence — and the same `hasPolylineWidth`
 * gate, the same "no loops ⇒ fall back" rule — is mirrored exactly here.
 */

import {
  hasPolylineWidth,
  heavyPolylineWidths,
  polylineSubtype,
  type PolylineEntity,
  type Vec3,
} from "@dxf-ai-editor/drawing-model";

import { segmentsFromPoints, type Vec2 } from "./common";
import {
  heavyPolylineRuns,
  heavyPolylineWcsResolver,
} from "./heavyPolylineTopology";
import {
  polylineCentrelineSamples,
  widePolylineBandLoops,
} from "./polylineWidthBand";
import type { SelectSegment } from "./types";

/** Point/segment select-hit geometry (the shape `./selectLeaf.ts` returns). */
interface HeavyPolylineSelectGeometry {
  points: Vec2[];
  segments: SelectSegment[];
}

/** One drawn run in WCS: a band silhouette ring, or a centreline sample chain. */
export interface HeavyPolylineDrawnRun {
  readonly points: readonly Vec3[];
  /** A band ring is always closed; a centreline inherits the run's own flag. */
  readonly closed: boolean;
}

/**
 * THE runs a heavy POLYLINE is drawn as, in WCS — the band SILHOUETTE for every
 * run that declares a width, the tessellated CENTRELINE for every run that does
 * not, each through the shared call `batchedLayer.ts` makes for the same
 * geometry. Mesh wire runs come back as their own centrelines.
 */
export function heavyPolylineDrawnRuns(
  entity: PolylineEntity,
): HeavyPolylineDrawnRun[] {
  const out: HeavyPolylineDrawnRun[] = [];

  const subtype = polylineSubtype(entity);
  // ONLY the 2D subtype carries WIDTH, and only it has an OCS plane.
  //
  // WIDTH: groups 40/41 are `AcDb2dPolyline` / `AcDb2dVertex` fields, so a 3D
  // polyline and both MESH subtypes are drawn at width 0 however their records
  // are written (`ezdxf`: `Polyline.has_width` returns `False` unless
  // `is_2d_polyline`; `TraceBuilder.from_polyline` refuses a non-2D POLYLINE
  // outright). That rule lives in `drawing-model`'s `heavyPolylineWidths`
  // itself, so this walk no longer pre-filters the mesh subtypes on its own —
  // the resolver answers all-zero pairs for every non-2D subtype and
  // `hasPolylineWidth` below drops them, which now covers the 3D polyline the
  // old mesh-only filter let through (@codex PR #785 round 12, F2). One copy
  // of the rule, not one per consumer.
  //
  // PLANE: a 3D polyline / mesh stores REAL WCS vertices and the shared
  // resolver leaves them alone, so declaring a plane for one would tilt
  // geometry that was never in any (`polylineFromEntity.ts` states the same
  // rule for the render side). A DEFAULT extrusion costs nothing either way —
  // `polylinePlane` short-circuits it to `null`.
  //
  // DOCUMENTED EQUIVALENT MUTANT (targeted mutation testing), NEW in @codex
  // PR #785 round 12:
  //   file:line        this file, the `subtype === "2d"` extrusion gate
  //   mutator          ConditionalExpression → `true` (declare the plane for
  //                    EVERY subtype)
  //   why equivalent   `extrusion` reaches exactly two calls below, and for a
  //                    non-2D subtype NEITHER reads it. The band arm is
  //                    unreachable (the width gate above resolves every non-2D
  //                    record to 0), and `polylineCentrelineSamples` returns
  //                    its input points UNCHANGED — never touching
  //                    `polylinePlane(extrusion)` — when no span is bulged,
  //                    which is every non-2D run by construction.
  //   the invariant    `heavyPolylineRuns` emits `bulges` as
  //                    `is2d ? (vertex.bulge ?? 0) : 0` (group 42 is a
  //                    2D-polyline field), so a 3D polyline / polygon mesh /
  //                    poly-face mesh has NO non-zero bulge to evaluate in any
  //                    plane
  //   proved by        `./heavyPolylineTopology.test.ts` + this file's own
  //                    "a polygon MESH is a WIREFRAME", "a POLY-FACE mesh is a
  //                    wireframe too" and "a 3D polyline has NO band at all"
  //                    cases in `./heavyPolylineSelectDrawnShape.test.ts`
  // BEFORE round 12 this mutant WAS killed — by the very assertion the round
  // corrected: a 3D polyline used to be given a band, and a `true` gate then
  // tilted that band through its group-210. With the band gone the extrusion
  // has nothing left to tilt. The gate stays because it states the rule the
  // plane belongs to the 2D subtype, and a future topology change that gave a
  // 3D run a bulge must not silently start lifting WCS vertices.
  const extrusion: Vec3 | undefined =
    subtype === "2d" ? entity.extrusion : undefined;
  const pointOf = heavyPolylineWcsResolver(entity);

  for (const run of heavyPolylineRuns(entity)) {
    // Index and bulge travel TOGETHER through the defensive drop, so a missing
    // record cannot slide the remaining bulges onto the wrong spans.
    //
    // DOCUMENTED EQUIVALENT MUTANT (targeted mutation testing):
    //   file:line        this file, the `entity.vertices[i] === undefined` guard
    //   mutator          ConditionalExpression → `false`
    //   why equivalent   the guard never fires through this caller, so dropping
    //                    it changes nothing observable
    //   the invariant    `heavyPolylineRuns`' contract is that every emitted
    //                    index addresses a REAL vertex record
    //   proved by        `./polylineMeshTopology.test.ts`'s "every emitted index
    //                    addresses a REAL vertex record"
    // It stays because THIS function must not read `undefined.location` if a
    // future topology change, or a direct caller, ever relaxes that contract.
    const indices: number[] = [];
    const bulges: number[] = [];
    run.indices.forEach((i, k) => {
      if (entity.vertices[i] === undefined) return;
      indices.push(i);
      bulges.push(run.bulges[k] ?? 0);
    });
    if (indices.length === 0) continue;

    const wcs = indices.map((i) => pointOf(i));

    // Per-vertex widths (40/41) over the POLYLINE record's own defaults —
    // `drawing-model`'s `polylineWidth.ts`, the ONE place that rule is written,
    // read through the SAME visible-record `indices` the points are so a
    // spline-fit polyline's widths stay aligned with its generated run.
    const resolved = heavyPolylineWidths(entity, indices);
    const widths = hasPolylineWidth(resolved) ? resolved : [];
    if (widths.length > 0) {
      const loops = widePolylineBandLoops(
        wcs,
        run.closed,
        bulges,
        widths,
        extrusion,
      );
      if (loops.length > 0) {
        // Each loop is a CLOSED silhouette ring (an open run yields one ring
        // including its two square caps; a closed run yields the outer and
        // inner edge of the annulus).
        for (const loop of loops) out.push({ points: loop, closed: true });
        continue;
      }
      // No live loops — every span degenerate in its own plane. The canvas
      // strokes the centreline in exactly that case, so fall through to it.
    }

    out.push({
      points: polylineCentrelineSamples(wcs, run.closed, bulges, extrusion),
      closed: run.closed,
    });
  }

  return out;
}

/**
 * The window / crossing select geometry: {@link heavyPolylineDrawnRuns}
 * projected to the hit test's plane.
 *
 * The projection happens HERE and nowhere earlier — that ordering IS the fix.
 * Flattening to XY before the arc walk or the band offset is exactly what put a
 * tilted entity's bow on the wrong world axis.
 */
export function heavyPolylineSelectGeometry(
  entity: PolylineEntity,
): HeavyPolylineSelectGeometry {
  const points: Vec2[] = [];
  const segments: SelectSegment[] = [];
  for (const run of heavyPolylineDrawnRuns(entity)) {
    const flat: Vec2[] = run.points.map((p) => ({ x: p.x, y: p.y }));
    points.push(...flat);
    segments.push(...segmentsFromPoints(flat, run.closed));
  }
  return { points, segments };
}
